import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLImageElement} [productImage] - Product image element
 */

/**
 * A custom element that manages the sticky product bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * On this shop the bar is NOT a buy button: clicking it scrolls the shopper to
 * the place where the product is configured. It never adds to cart, never
 * submits the product form and never navigates to checkout. When no target can
 * be resolved the click is a deliberate no-op.
 *
 * Target resolution, evaluated at click time inside the bar's own section:
 *   1. the product customizer shell rendered by the amavi-personalizador-*
 *      blocks, i.e. `[data-amp].ampw`, with `[data-amp]` and `.ampw` as
 *      loosened fallbacks in case a block was edited and dropped one of the
 *      two. The add-ons shell of amavi-extras-ifly shares those hooks but is
 *      not a customizer -- it only holds gift extras and it sits below the
 *      variant picker -- so `.ampw--ifly` is excluded here;
 *   2. the theme's `<variant-picker>`;
 *   3. the add-ons shell, for a product that has neither a customizer nor a
 *      variant picker;
 *   4. the buy buttons block, as a last resort.
 * A section setting can prepend one extra selector to that list.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /**
   * Ordered fallbacks for the scroll target. First visible match inside the
   * bar's own `.shopify-section` wins.
   * @type {string[]}
   */
  static targetSelectors = [
    '[data-amp].ampw:not(.ampw--ifly)',
    '[data-amp]:not(.ampw--ifly)',
    '.ampw:not(.ampw--ifly)',
    'variant-picker',
    '[data-amp].ampw',
    '.buy-buttons-block',
  ];

  /** Elements the target must not live inside. */
  static excludedAncestors = 'sticky-add-to-cart, product-card, quick-add-modal, .quick-add-modal';

  /** Breathing room left between the sticky header and the scroll target. */
  static scrollGap = 16;

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    // One delegated listener on the host covers both the button and the bar
    // around it. The bar matters because the dos-miradas customizer disables
    // the button while the two stones are still unpicked, and a disabled
    // button swallows pointer events; the stylesheet gives such a button
    // `pointer-events: none` so the click lands on the bar instead and the
    // shortcut keeps working exactly when the shopper needs it most.
    this.addEventListener('click', this.#handleBarClick, { signal });

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  // Public action handlers
  /**
   * Scrolls the shopper to the product customizer. Public so the theme editor
   * and other scripts can trigger the same behaviour.
   * @returns {boolean} Whether a target was found and scrolled to.
   */
  scrollToCustomizer() {
    const target = this.#resolveScrollTarget();

    // No target: do nothing at all. A missing customizer must never degrade
    // into a purchase.
    if (!target) return false;

    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - this.#stickyHeaderOffset());
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    window.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    return true;
  }

  /**
   * Handles a click anywhere on the sticky bar.
   * @param {MouseEvent} event
   */
  #handleBarClick = (event) => {
    const { target } = event;

    // Leave genuinely interactive controls inside the bar alone, should any be
    // added later. Nothing here ever submits a form.
    if (target instanceof Element) {
      const interactive = target.closest('a[href], input, select, textarea, summary, [role="link"]');
      if (interactive && this.contains(interactive)) return;
    }

    event.preventDefault();
    this.scrollToCustomizer();
  };

  /**
   * Resolves where the bar should scroll to, on the live page.
   * @returns {HTMLElement | null}
   */
  #resolveScrollTarget() {
    const scope = this.closest('.shopify-section') ?? document;
    const configured = (this.dataset.scrollTargetSelector ?? '').trim();
    const selectors = configured
      ? [configured, ...StickyAddToCartComponent.targetSelectors]
      : StickyAddToCartComponent.targetSelectors;

    for (const selector of selectors) {
      /** @type {NodeListOf<Element>} */
      let matches;

      try {
        matches = scope.querySelectorAll(selector);
      } catch {
        // A malformed selector typed in the theme editor must not break the bar.
        continue;
      }

      for (const candidate of matches) {
        if (!(candidate instanceof HTMLElement)) continue;
        if (candidate.closest(StickyAddToCartComponent.excludedAncestors)) continue;
        // Skips panels the customizer has moved out of the product column or
        // hidden, e.g. the dos-miradas stone modal that is reparented to <body>.
        if (candidate.getClientRects().length === 0) continue;
        return candidate;
      }
    }

    return null;
  }

  /**
   * Height already covered by a sticky/fixed header, so the target doesn't end
   * up underneath it.
   * @returns {number}
   */
  #stickyHeaderOffset() {
    let bottom = 0;

    for (const element of document.querySelectorAll('#header-group, #header-component, .header-group, header')) {
      if (!(element instanceof HTMLElement)) continue;
      const { position } = getComputedStyle(element);
      if (position !== 'sticky' && position !== 'fixed') continue;
      bottom = Math.max(bottom, element.getBoundingClientRect().bottom);
    }

    if (bottom > 0) {
      // theme.liquid measures the whole header group; use it as a floor when it
      // is taller than whatever single element we found sticky.
      const measured = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height'));
      if (Number.isFinite(measured)) bottom = Math.max(bottom, measured);
    }

    return bottom + StickyAddToCartComponent.scrollGap;
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky bar HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
